import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Heading, Icon, Link, Text } from '@amzn/storm-ui';
import { BenefitItem, ActionCard } from './components';
import UpgradeProgressNotifier from './components/UpgradeProgressNotifier';
import PendingRequestsCard from './components/PendingRequestsCard';
import CombineProgressNotifier from './components/CombineProgressNotifier';
import {
  combineAccountsIcon,
  expandGloballyIcon,
  streamlineOperationsIcon,
} from './icons';
import {
  COMBINE_ACCOUNTS_CHOOSE_PATH,
  UPGRADE_ACCOUNTS_CHOOSE_PATH,
} from 'src/shared/routes';
import useIntl from '@i18n/useIntl';
import useChrome from '@chrome/hooks/useChrome';
import { ChromeKeys } from '@chrome/enums';
import {
  COMBINE_LANDING_LEARN_MORE_KEY,
  COMBINE_LANDING_HEADING_KEY,
  COMBINE_LANDING_BENEFIT_FULLFUNNEL_HEADING_KEY,
  COMBINE_LANDING_BENEFIT_FULLFUNNEL_DESCRIPTION_KEY,
  COMBINE_LANDING_BENEFIT_GLOBAL_HEADING_KEY,
  COMBINE_LANDING_BENEFIT_GLOBAL_DESCRIPTION_KEY,
  COMBINE_LANDING_BENEFIT_STREAMLINE_HEADING_KEY,
  COMBINE_LANDING_BENEFIT_STREAMLINE_DESCRIPTION_KEY,
  COMBINE_LANDING_ACTION_COMBINE_HEADING_KEY,
  COMBINE_LANDING_ACTION_COMBINE_DESCRIPTION_KEY,
  COMBINE_LANDING_ACTION_COMBINE_LINK_KEY,
  COMBINE_LANDING_ACTION_UPGRADE_HEADING_KEY,
  COMBINE_LANDING_ACTION_UPGRADE_DESCRIPTION_KEY,
  COMBINE_LANDING_ACTION_UPGRADE_LINK_KEY,
} from '@features/combine-accounts/constants';

const COMBINATIONS_LANDING_PAGE_ID = 'combinations-landing-page';

// TODO: replace placeholder link with actual help URL
/*
  The Advertiser accounts help page. Absolute and external, so it is opened in its own tab
  rather than navigated to: this page is mid-task for anyone reading it, and replacing the
  page with help would lose whatever they were part-way through.

  A new tab needs rel="noopener noreferrer" alongside it. Without noopener the opened page
  can reach back through window.opener and redirect the tab it came from, so a reader who
  returns lands somewhere they never asked for, and without noreferrer the console's URL
  leaves with the request.
*/
const LEARN_MORE_URL = 'https://advertising.amazon.com/help/GXKXR56ZCMJ3WCS4';

const CombineLandingPage: FC = () => {
  const navigate = useNavigate();
  const { intl } = useIntl();

  /*
    The landing page does not render CombineAccountsLayout, so it needs its own
    mount: without this it inherits the chrome default and binds the switcher to
    whichever entity the session carries, even though the page is a cross-account
    entry point.
  */
  useChrome(ChromeKeys.AccountCombination);

  return (
    <Box
      id={COMBINATIONS_LANDING_PAGE_ID}
      data-testid={COMBINATIONS_LANDING_PAGE_ID}
      paddingBlockStart="medium"
      paddingBlockEnd="medium"
      paddingInlineStart="medium"
      paddingInlineEnd="medium"
    >
      {/*
        Reports an upgrade that was started on the choose page and outlived the
        short window, so the user is told here rather than on a page they have
        already left. Renders nothing when there is no upgrade to report, which is
        every ordinary visit.
      */}
      <UpgradeProgressNotifier />

      {/*
        The same for a combine started on the review page. Separate from the upgrade notifier rather
        than folded into it: the two lanes hand over different things and end differently, since a
        finished combine redirects into the account while a finished upgrade is reported here. Also
        renders nothing on an ordinary visit.
      */}
      <CombineProgressNotifier />

      <Flex
        justifyContent="space-between"
        alignItems="center"
        paddingBlockEnd="medium"
      >
        <Heading renderAs="h3" typeface="display">
          {intl.getMessage(COMBINE_LANDING_HEADING_KEY)}
        </Heading>
        <Link href={LEARN_MORE_URL} target="_blank" rel="noopener noreferrer">
          <Flex alignItems="center" columnGap="xxxsmall">
            <Icon type="info-circle" />
            <Text>{intl.getMessage(COMBINE_LANDING_LEARN_MORE_KEY)}</Text>
          </Flex>
        </Link>
      </Flex>

      <Box
        paddingBlockEnd="medium"
        paddingInlineStart="xlarge"
        paddingInlineEnd="xlarge"
      >
        <Flex columnGap="medium">
          <BenefitItem
            icon={
              <img src={combineAccountsIcon} alt="" width={60} height={60} />
            }
            heading={intl.getMessage(
              COMBINE_LANDING_BENEFIT_FULLFUNNEL_HEADING_KEY,
            )}
            description={intl.getMessage(
              COMBINE_LANDING_BENEFIT_FULLFUNNEL_DESCRIPTION_KEY,
            )}
          />
          <BenefitItem
            icon={
              <img src={expandGloballyIcon} alt="" width={60} height={60} />
            }
            heading={intl.getMessage(
              COMBINE_LANDING_BENEFIT_GLOBAL_HEADING_KEY,
            )}
            description={intl.getMessage(
              COMBINE_LANDING_BENEFIT_GLOBAL_DESCRIPTION_KEY,
            )}
          />
          <BenefitItem
            icon={
              <img
                src={streamlineOperationsIcon}
                alt=""
                width={60}
                height={60}
              />
            }
            heading={intl.getMessage(
              COMBINE_LANDING_BENEFIT_STREAMLINE_HEADING_KEY,
            )}
            description={intl.getMessage(
              COMBINE_LANDING_BENEFIT_STREAMLINE_DESCRIPTION_KEY,
            )}
          />
        </Flex>
      </Box>

      {/*
        Pending combination requests, in both directions. Sits between the
        benefits and the two actions as the design places it, and removes itself
        entirely when the user has no requests, which is every ordinary visit.
      */}
      <PendingRequestsCard />

      <Flex columnGap="medium">
        <ActionCard
          heading={intl.getMessage(COMBINE_LANDING_ACTION_COMBINE_HEADING_KEY)}
          description={intl.getMessage(
            COMBINE_LANDING_ACTION_COMBINE_DESCRIPTION_KEY,
          )}
          linkText={intl.getMessage(COMBINE_LANDING_ACTION_COMBINE_LINK_KEY)}
          href={COMBINE_ACCOUNTS_CHOOSE_PATH}
          onClick={(e) => {
            e.preventDefault();
            navigate(COMBINE_ACCOUNTS_CHOOSE_PATH);
          }}
        />
        {/* TODO: wire up upgrade flow once route is available */}
        <ActionCard
          heading={intl.getMessage(COMBINE_LANDING_ACTION_UPGRADE_HEADING_KEY)}
          description={intl.getMessage(
            COMBINE_LANDING_ACTION_UPGRADE_DESCRIPTION_KEY,
          )}
          linkText={intl.getMessage(COMBINE_LANDING_ACTION_UPGRADE_LINK_KEY)}
          href={UPGRADE_ACCOUNTS_CHOOSE_PATH}
          onClick={(e) => {
            e.preventDefault();
            navigate(UPGRADE_ACCOUNTS_CHOOSE_PATH);
          }}
        />
      </Flex>
    </Box>
  );
};

export default CombineLandingPage;
